import { useState, useEffect } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import Canvas from '../canvas/Canvas';
import ChatPanel from '../chat/ChatPanel';

export default function Dashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Open chat by default on desktop
  useEffect(() => {
    if (!isMobile) {
      setIsChatOpen(true);
    }
  }, [isMobile]);

  return (
    <div className="h-screen bg-grey-light flex flex-col overflow-hidden">
      {/* Header */}
      <Header
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        onToggleChat={() => setIsChatOpen(!isChatOpen)}
        isChatOpen={isChatOpen}
        isMobile={isMobile}
      />

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebar */}
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        {/* Main area with canvas and chat */}
        <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* Canvas area - Full width on mobile, flex-1 on desktop */}
          <div className="flex-1 overflow-auto transition-all duration-200">
            <Canvas />
          </div>

          {/* Chat panel - Bottom sheet on mobile, side panel on desktop */}
          {isMobile ? (
            // Mobile: Bottom sheet overlay
            <>
              {/* Backdrop */}
              <div
                className={`fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity duration-200 ${
                  isChatOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                style={{ zIndex: 200 }}
                onClick={() => setIsChatOpen(false)}
              />
              {/* Bottom sheet */}
              <div
                className={`fixed inset-x-0 bottom-0 bg-white border-t border-grey-medium/20 shadow-modal transition-transform duration-300 ease-out ${
                  isChatOpen ? 'translate-y-0' : 'translate-y-full'
                }`}
                style={{ zIndex: 300, height: '70vh', maxHeight: 'calc(100vh - 56px)' }}
              >
                {/* Drag handle */}
                <div className="flex justify-center py-2 cursor-grab active:cursor-grabbing" onClick={() => setIsChatOpen(false)}>
                  <div className="w-10 h-1 bg-grey-medium/40 rounded-full" />
                </div>
                <div className="h-[calc(100%-16px)] overflow-hidden">
                  <ChatPanel />
                </div>
              </div>
            </>
          ) : (
            // Desktop: Side panel
            <div
              className={`transition-all duration-200 ease-out border-l border-grey-medium/20 bg-white ${
                isChatOpen ? 'w-96 lg:w-[420px]' : 'w-0'
              } overflow-hidden flex-shrink-0`}
            >
              {isChatOpen && (
                <div className="animate-fade-in h-full">
                  <ChatPanel />
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Mobile: Floating chat button when chat is closed */}
      {isMobile && !isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-4 right-4 w-12 h-12 bg-mint text-black rounded-full shadow-elevated flex items-center justify-center hover:bg-mint/90 active:scale-95 transition-all"
          style={{ zIndex: 150 }}
          aria-label="Open chat"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
